/**
 * SMS FCM registration transport (SMS binding).
 *
 * Endpoint mechanics live in `@aegix/web-push` (registration is
 * a POST to `rest/notifications/fcm/`, deregistration a **PUT** to
 * `rest/notifications/fcm/deregister`, both carrying `x-device-id` so the
 * backend can sweep this device's stale registrations). This module binds
 * them to SMS's authenticated ky transport, keeping the app's real
 * middleware in the loop — 401-queue refresh, `x-refreshed-token`
 * rotation, vendor-org middleware — instead of hand-rolling cookie/header
 * handling (the `jwt-auth-sms` cookie is parent-scope shared; never touch
 * it directly).
 */
import {
  createFcmApi,
  type WebPushRegistrationPayload,
  type WebPushRegistrationResponse,
} from '@aegix/web-push';
import { client } from '../lib/http-client';

/** Registration payload (mirrors AIM's `FCMType` + the web fields). */
export type SmsFcmRegistrationPayload = WebPushRegistrationPayload;

export type SmsFcmRegistrationResponse = WebPushRegistrationResponse;

const api = createFcmApi(client.ky);

export async function registerFcmToken(
  payload: SmsFcmRegistrationPayload,
): Promise<SmsFcmRegistrationResponse> {
  const response = await api.register(payload);
  return (await response.json()) as SmsFcmRegistrationResponse;
}

export async function deregisterFcmToken(
  token: string,
  deviceId: string,
): Promise<void> {
  await api.deregister(token, deviceId);
}
